import { Permissions } from '@anessa/shared-models';
import type {
  KpiRecomputeOutcome,
  KpiRecomputeStatus,
} from '../../features/dashboard/services/kpi-api/kpi-api.service';

/**
 * Secret `~` dev-command menu, restricted to @anessa.com accounts (see
 * ANESSA_EMAIL_DOMAIN below). Purely a testing/QA accelerator for shortcuts
 * to features a user can already reach through the normal UI — never a way
 * to bypass anything. Intentionally has no i18n: these strings are only ever
 * shown to internal staff, not real customers.
 *
 * New commands must not reimplement behavior — call into the existing
 * service/component that already does it (e.g. 'changeproject' just calls
 * `ProjectService.openProject()`, the same method the real plant switcher
 * uses). A dev command is a shortcut to real, already-tested functionality,
 * not a second implementation of it. ('notes' is the one deliberate exception —
 * there's no existing notepad feature to shortcut to, so it owns its own tiny
 * bit of state instead.)
 */

export const DEV_MODE_TRIGGER = '~';
// Shared with the backend, which enforces the same check for the month-lock hatch.
export { ANESSA_EMAIL_DOMAIN, isAnessaEmail } from '@anessa/shared-models';

export type DevCommandKind =
  /** Runs and closes the palette immediately (e.g. toggling a setting). */
  | 'immediate'
  /** Needs a typed-out argument — seeds "~<id> " and shows an autosuggest list. */
  | 'argument'
  /** Shows read-only info in place, no argument or list to select from. */
  | 'info';

export interface DevCommand {
  id: string;
  label: string;
  icon: string;
  kind: DevCommandKind;
  /**
   * Same shape as CommandPalettePage.permission. Most dev commands don't need
   * this (switching to a plant you already have access to, or toggling your
   * own theme, isn't gated by anything) — but any command that maps to a
   * gated action (like 'recompute') must set this so it respects the same
   * permission the rest of the app already enforces.
   */
  permission?: string | string[];
}

export const DEV_COMMANDS: DevCommand[] = [
  // "Project" (not "plant") since a project can be single- or multi-plant —
  // the autosuggest list distinguishes them with different icons.
  { id: 'changeproject', label: 'Change Project', icon: 'swap_horiz', kind: 'argument' },
  // Opens the real create-project dialog immediately — see
  // CommandPalette.openCreateProjectDialog().
  { id: 'newplant', label: 'New Project', icon: 'add', kind: 'immediate' },
  // Calls MetricsCommService.recompute() directly — the same endpoint the
  // per-metric "recompute" button in the metrics manager calls.
  {
    id: 'recompute',
    label: 'Recompute Metric',
    icon: 'calculate',
    kind: 'argument',
    permission: Permissions.RECOMPUTE_METRICS,
  },
  // KpiApiService.recomputeSweepAndWait() — POSTs /api/kpis/recompute/sweep,
  // recomputing the trailing window (90 days) of materialized KPI values for
  // the active plant. Normally driven by the nightly cron; this is the manual
  // trigger for after a correction, or to seed a plant for the first time. The
  // sweep runs in the background server-side, so the command polls
  // /api/kpis/recompute/status and reports the outcome when it lands.
  {
    id: 'recomputekpis',
    label: 'Recompute Custom KPIs (90d)',
    icon: 'insights',
    kind: 'immediate',
    permission: Permissions.GLOBAL_ADMIN,
  },
  // KpiApiService.recomputeStatus() — one GET /api/kpis/recompute/status: the
  // recompute running now, or how the last one ended. Lets you check back on a
  // sweep that outlasted 'recomputekpis' polling. Only sweeps started from the
  // palette report there; the nightly cron doesn't.
  {
    id: 'kpistatus',
    label: 'Custom KPI Recompute Status',
    icon: 'pending_actions',
    kind: 'immediate',
    permission: Permissions.GLOBAL_ADMIN,
  },
  // The month lock's escape hatch (MonthLockService.openUnlock → POST
  // /api/month-lock/unlocks): "~unlock 2026-10 <reason>" unlocks one locked
  // month of the current plant for you only, for RED_RULES.unlockMinutes,
  // after a confirmation. Enforced server-side (Anessa accounts only); every
  // change made meanwhile is audited.
  { id: 'unlock', label: 'Unlock Locked Month', icon: 'lock_open', kind: 'argument' },
  // MonthLockService.addDelegate() — "~unlockadd marie@client.fr" lets that
  // person write into your unlocked month too (e.g. a client making the
  // correction), up to RED_RULES.unlockMaxDelegates, after a confirmation.
  { id: 'unlockadd', label: 'Add Person to Unlock', icon: 'person_add', kind: 'argument' },
  // MonthLockService.relock() — closes your open unlock early, for everyone on it.
  { id: 'relock', label: 'Relock Month', icon: 'lock', kind: 'immediate' },
  // MonthLockStatusDialog — the current plant's month lock: start month,
  // locked-through date, next lock and where the latest months stand. Read-only.
  { id: 'lockstatus', label: 'Month Lock Status', icon: 'event_busy', kind: 'immediate' },
  // RedApiService.proposalPreview() — "~redpreview 2026-08": what a re-check of
  // that locked month would propose right now, in the RED review dialog with
  // no decision buttons. Read-only (it doesn't even take a first snapshot);
  // Anessa accounts only, enforced server-side.
  { id: 'redpreview', label: 'Preview RED Proposal', icon: 'rate_review', kind: 'argument' },
  // RedApiService.history() — "~redhistory 2026-09": every version that locked
  // month has had, newest first (accepted, superseded, and the proposals that
  // were rejected or withdrawn, with who decided and why), each opening
  // read-only in the RED review dialog against the version before it.
  // Read-only; Anessa accounts only, enforced server-side.
  { id: 'redhistory', label: 'RED Version History', icon: 'history', kind: 'argument' },
  // RedSchedulerClaimService.claim() — the backend this browser talks to (your
  // local one when testing locally) alone runs the RED scheduled jobs
  // (correction proposals, daily snapshots) for RED_RULES.schedulerClaimMinutes,
  // so other backends on a shared database can't build them with their own
  // code. Anessa accounts only, enforced server-side. Running it again extends it.
  { id: 'redclaim', label: 'Claim RED Scheduling', icon: 'schedule', kind: 'immediate' },
  // RedSchedulerClaimService.release() — ends your claim: every backend runs them again.
  { id: 'redrelease', label: 'Release RED Scheduling', icon: 'schedule', kind: 'immediate' },
  // Copies the current project id to the clipboard.
  { id: 'copyid', label: 'Copy Project ID', icon: 'content_copy', kind: 'immediate' },
  // Copies DashboardService.selectedDashboardID() — a no-op if none is open.
  {
    id: 'copydashboardid',
    label: 'Copy Dashboard ID',
    icon: 'content_copy',
    kind: 'immediate',
  },
  // Autosuggests DashboardService.dashboardList() and navigates to '/dashboard/<id>' —
  // the same route the sidebar's own dashboard links use.
  { id: 'opendashboard', label: 'Open Dashboard', icon: 'dashboard', kind: 'argument' },
  // PermissionService.invalidateAndRefresh() — the same call role/permission
  // changes already trigger; a manual escape hatch without logging out.
  {
    id: 'refreshperms',
    label: 'Refresh Permissions',
    icon: 'sync',
    kind: 'immediate',
  },
  // Global-admin only: preview the whole UI as another role via
  // PermissionService.setPreviewRole() — a read-time lens that only ever hides
  // UI (the backend still authorizes the real admin). The banner or the list's
  // "Exit preview" entry ends it.
  {
    id: 'changerole',
    label: 'Change Role (preview)',
    icon: 'badge',
    kind: 'argument',
    permission: Permissions.GLOBAL_ADMIN,
  },
  // TabViewService.toggle() — shows/hides the experimental browser-tab-style
  // tab bar (TabBar) and switches the RouteReuseStrategy that keeps its tabs hot.
  { id: 'tabs', label: 'Toggle Tab View', icon: 'tab', kind: 'immediate' },
  // TabViewService.replaceTabs() — "~usetabs /a /b …" replaces the open tabs
  // with that list of in-app paths (turning tab view on if needed), e.g. to
  // open every page a change touches in one paste.
  { id: 'usetabs', label: 'Use Tab List', icon: 'tab', kind: 'argument' },
  // Copies the open tabs as a ready-to-paste "~usetabs …" line.
  { id: 'copytabs', label: 'Copy Tab List', icon: 'content_copy', kind: 'immediate' },
  // SessionService.updateUserTheme() — the same call the user-menu toggle makes.
  { id: 'theme', label: 'Toggle Theme', icon: 'dark_mode', kind: 'immediate' },
  // SessionService.updateUserLanguage() — the same call the topbar language switcher makes.
  { id: 'lang', label: 'Toggle Language', icon: 'translate', kind: 'immediate' },
  // Cycles the sidebar "missing routing" notification dots through their test
  // states (none → both → approvisionnements → expeditions → off) via
  // NotificationDotTestService, so the dots can be previewed without real
  // receptions/expeditions missing their routing. QA aid only.
  {
    id: 'notifdots',
    label: 'Cycle Notification Dots',
    icon: 'notifications',
    kind: 'immediate',
  },
  // FifoPreviewSettingsService.toggle() — shows/hides the FIFO preview button
  // on the incorporation saisie pages. Off by default, persisted to
  // localStorage; the button itself is still restricted to anessa staff
  // regardless of this toggle.
  {
    id: 'fifopreview',
    label: 'Toggle FIFO Preview',
    icon: 'preview',
    kind: 'immediate',
  },
  // A single persistent note, edited in its own textarea (see command-palette.ts) —
  // localStorage-only, deliberately not synced to the backend; a scratchpad,
  // not a real feature. 'info' kind: no argument in the top input — the
  // textarea is a separate, dedicated editing surface.
  { id: 'notes', label: 'Notes', icon: 'edit_note', kind: 'info' },
];

/**
 * What to tell the operator when a Custom KPI recompute ends.
 *
 * Every branch says something: the recompute is started with a 202 and watched
 * by polling, so a run that is refused, fails, or outlasts the poll would
 * otherwise end in silence — which is exactly the failure mode this replaced
 * (the request used to 504 while the sweep quietly finished server-side).
 */
export function recomputeOutcomeMessage(outcome: KpiRecomputeOutcome): string {
  switch (outcome.kind) {
    case 'busy':
      return `A Custom KPI recompute is already running${plantSuffix(outcome.plantId)}`;
    case 'timeout':
      return 'Custom KPI recompute is still running - check back shortly';
    case 'finished':
      return finishedMessage(outcome.status);
  }
}

function finishedMessage(status: KpiRecomputeStatus): string {
  if (status.state === 'failed') {
    return `Custom KPI recompute failed: ${status.error ?? 'unknown error'}`;
  }
  // "unit" not "day": the sweep covers every domain, so a 90-day window over
  // three domains is 270 units. Calling those days reads as a bug.
  return `Recomputed ${status.units ?? 0} domain-day(s) of Custom KPIs${plantSuffix(status.plantId)}`;
}

/** What `~kpistatus` tells the operator about the backend's recompute. */
export function recomputeStatusMessage(status: KpiRecomputeStatus): string {
  const plant = plantSuffix(status.plantId);
  switch (status.state) {
    case 'idle':
      return 'No Custom KPI recompute since the server started';
    case 'running':
      return `Custom KPI recompute running${plant}${atTime(' since', status.startedAt)}`;
    case 'succeeded':
      return `Last Custom KPI recompute${plant} finished${atTime('', status.finishedAt)}: ${status.units ?? 0} domain-day(s)`;
    case 'failed':
      return `Last Custom KPI recompute${plant} failed${atTime(' at', status.finishedAt)}: ${status.error ?? 'unknown error'}`;
  }
}

/** " since 14:02" style suffix in local time, or nothing without a timestamp. */
function atTime(prefix: string, iso?: string): string {
  if (!iso) return '';
  const time = new Date(iso).toTimeString().slice(0, 5);
  return `${prefix} ${time}`;
}

function plantSuffix(plantId?: string): string {
  return plantId ? ` for ${plantId}` : '';
}
